/**
 * Allowlist HTML sanitizer for session comments.
 *
 * Comments are authored in a TipTap editor that emits HTML (`editor.getHTML()`),
 * so the stored value genuinely is markup and cannot be rendered as plain text.
 * But the same column is writable straight through PostgREST with an arbitrary
 * string, and the row is rendered to every viewer of a public session — including
 * signed-out visitors. Without a sanitizer that is stored XSS.
 *
 * Deliberately dependency-free: the repository ships no sanitizer (no DOMPurify,
 * no sanitize-html) and adding one is a bigger change than this needs. The
 * parsing below is inert — DOMParser builds a document that is never attached to
 * the live DOM, so `<img onerror>` does not fire while we inspect it.
 *
 * Run it on write *and* on render. On render it also cleans rows stored before
 * this fix existed.
 */

const ALLOWED_TAGS = new Set([
  "p", "br", "hr", "div",
  "strong", "b", "em", "i", "u", "s", "del", "mark", "sub", "sup",
  "ul", "ol", "li",
  "blockquote", "pre", "code",
  "h1", "h2", "h3", "h4",
  "a", "span", "img",
]);

/**
 * A mention is the one stored element that carries data: the comment editor
 * writes `<span data-mention="vocabulary" data-mention-id="…">@word</span>` so a
 * reader can jump to that item. Both values are re-validated here — the kind
 * against the closed set, the id against the uuid-or-expression-slug alphabet —
 * because the column is writable through PostgREST with anything at all. The
 * label is the span's text, which needs no permission of its own.
 */
const MENTION_KINDS = new Set(["question", "vocabulary", "expression"]);
const MENTION_ID = /^[A-Za-z0-9_-]{1,80}$/;

/** tag -> attribute -> optional value transform */
const ALLOWED_ATTRS: Record<string, Record<string, (value: string) => string | null>> = {
  a: {
    href: safeHref,
    title: (v) => v,
    target: (v) => (v === "_blank" ? "_blank" : null),
    rel: () => "noopener noreferrer nofollow",
  },
  code: { class: (v) => (v.startsWith("language-") ? v : null) },
  span: {
    class: () => null,
    "data-mention": (v) => (MENTION_KINDS.has(v) ? v : null),
    "data-mention-id": (v) => (MENTION_ID.test(v) ? v : null),
  },
  img: {
    src: safeImageSrc,
    alt: (v) => v,
  },
};

/**
 * Comment images are stored in Cloudinary — the same service as practice
 * recordings, but as public images (`type=upload`) — and delivered from
 * `https://res.cloudinary.com/<cloud>/image/upload/...`.
 *
 * The rendered HTML goes to every viewer of a public session, so an `<img>` may
 * only point at that delivery path and only inside the `comment-images`
 * namespace the `upload-comment-image` function writes: another host, another
 * resource type, a `data:` URI or a `javascript:` URL is dropped.
 */
const CLOUDINARY_IMAGE_HOST = "res.cloudinary.com";

/**
 * `<cloud>/image/upload/[v<version>/]comment-images/<userId>/<uuid>.<ext>`
 * and nothing else. The `comment-images` segment is what marks the asset as ours
 * — the cloud name is not in the client bundle, so the namespace carries that
 * job here.
 */
const CLOUDINARY_COMMENT_IMAGE_PATH =
  /^\/[^/]+\/image\/upload\/(?:v\d+\/)?comment-images\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\.[A-Za-z0-9]+$/;

/**
 * Forced on every rendered image rather than trusted from stored markup: a
 * discussion image is cross-origin, and withholding the referrer is both the
 * privacy default for readers and what keeps hosts that reject hotlinking from
 * answering with an error page instead of the picture.
 */
const IMAGE_REFERRER_POLICY = "no-referrer";

function safeImageSrc(value: string): string | null {
  const trimmed = value.trim();
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "https:") return null;
    if (url.hostname !== CLOUDINARY_IMAGE_HOST) return null;
    if (!CLOUDINARY_COMMENT_IMAGE_PATH.test(url.pathname)) return null;
    return trimmed;
  } catch {
    // Not an absolute URL: there is no image to allow.
  }
  return null;
}

/**
 * The same rule the sanitizer applies, exposed so the editor can refuse an
 * image it could never store — otherwise a pasted external image would show
 * while composing and silently disappear on save.
 */
export function isAllowedCommentImageSrc(src: string): boolean {
  return safeImageSrc(src) !== null;
}

function safeHref(value: string): string | null {
  const trimmed = value.trim();
  // Relative and fragment links are same-origin by construction.
  if (trimmed.startsWith("/") || trimmed.startsWith("#")) return trimmed;
  try {
    const url = new URL(trimmed);
    if (url.protocol === "http:" || url.protocol === "https:" || url.protocol === "mailto:") {
      return trimmed;
    }
  } catch {
    // Not an absolute URL and not a path/fragment: drop it.
  }
  return null;
}

/** Tags whose *contents* are dangerous and must be removed with their children. */
const DROP_WITH_CHILDREN = new Set([
  "script", "style", "iframe", "object", "embed", "template", "noscript",
  "svg", "math", "link", "meta", "base", "form", "input", "button", "textarea",
  "select", "option",
]);

/** Tags to unwrap: keep the text, discard the element. */
const UNWRAP = new Set(["html", "head", "body", "table", "thead", "tbody", "tr", "td", "th", "font", "center"]);

function cleanNode(node: Element): void {
  // Walk the LIVE child list, not a snapshot. Unwrapping (and dropping an
  // unknown tag) hoists that element's children up into this node, so the pass
  // must re-examine the position it just vacated. A snapshot taken before the
  // mutation never visits the hoisted subtree, and everything below a
  // <font>/<center>/<table>/unknown wrapper then escapes the tag allowlist, the
  // attribute allowlist, DROP_WITH_CHILDREN and the forced image attributes —
  // `<font><img src=x onerror=…></font>` came out with the handler intact.
  let index = 0;
  while (index < node.childNodes.length) {
    const child = node.childNodes[index];

    if (child.nodeType === 3 /* text */) {
      index += 1;
      continue;
    }

    if (child.nodeType !== 1 /* element */) {
      child.remove();
      // The next node shifted into this index; do not advance.
      continue;
    }

    const el = child as Element;
    const tag = el.tagName.toLowerCase();

    if (DROP_WITH_CHILDREN.has(tag)) {
      el.remove();
      continue;
    }

    if (UNWRAP.has(tag) || !ALLOWED_TAGS.has(tag)) {
      // Keep the text, drop the element (unknown tags too). The hoisted
      // children now sit at this index and must be dispatched through the
      // allowlist themselves, so do not advance.
      while (el.firstChild) node.insertBefore(el.firstChild, el);
      el.remove();
      continue;
    }

    // Reachable handlers and inline styles survive nothing.
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const rule = ALLOWED_ATTRS[tag]?.[name];

      if (!rule) {
        el.removeAttribute(attr.name);
        continue;
      }

      const next = rule(attr.value);
      if (next === null) {
        el.removeAttribute(attr.name);
      } else {
        el.setAttribute(attr.name, next);
      }
    }

    // `target=_blank` always carries the opener guard.
    if (tag === "a" && el.getAttribute("target") === "_blank") {
      el.setAttribute("rel", "noopener noreferrer nofollow");
    }

    // An <img> whose src did not survive the allowlist is a broken frame — drop
    // the element instead of rendering an empty box in the comment.
    if (tag === "img" && !el.getAttribute("src")) {
      el.remove();
      continue;
    }

    // Forced, never inherited from the stored markup: see IMAGE_REFERRER_POLICY.
    if (tag === "img") {
      el.setAttribute("referrerpolicy", IMAGE_REFERRER_POLICY);
      el.setAttribute("loading", "lazy");
      el.setAttribute("decoding", "async");
    }

    // A mention whose id did not survive is a dead chip — it would still be
    // styled and still do nothing when clicked. Drop the marker so the label
    // ("@word") reads as the plain text it now is.
    if (tag === "span" && el.hasAttribute("data-mention") && !el.getAttribute("data-mention-id")) {
      el.removeAttribute("data-mention");
    }

    cleanNode(el);
    index += 1;
  }
}

/**
 * Returns an HTML string that is safe to hand to `dangerouslySetInnerHTML`.
 * Unparseable input degrades to an empty string rather than the raw value.
 */
export function sanitizeCommentHtml(dirty: string | null | undefined): string {
  if (!dirty) return "";

  // Server-side rendering / tests without a DOM: refuse rather than pass through.
  if (typeof DOMParser === "undefined") return "";

  try {
    const doc = new DOMParser().parseFromString(
      `<!doctype html><html><body><div id="sanitize-root">${dirty}</div></body></html>`,
      "text/html",
    );
    const root = doc.getElementById("sanitize-root");
    if (!root) return "";

    cleanNode(root);
    return root.innerHTML;
  } catch {
    return "";
  }
}

/** Strips all markup — for places that only need the text (notification previews). */
export function commentPreviewText(dirty: string | null | undefined): string {
  if (!dirty) return "";
  if (typeof DOMParser === "undefined") return "";
  try {
    const doc = new DOMParser().parseFromString(
      `<!doctype html><html><body><div id="preview-root">${dirty}</div></body></html>`,
      "text/html",
    );
    return doc.getElementById("preview-root")?.textContent?.trim() ?? "";
  } catch {
    return "";
  }
}
